import { API } from '../../../services'
import { APIResponse, Filters, Pagination } from '../../../services/types'
import { HttpStatusCode } from 'axios'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import { defineStore } from 'pinia'
import { setPagination } from '../../../services/utils/utils'
import { useToast } from 'vuestic-ui'
import type { InventoryItemApi, InventoryCreateItemApi } from '../types'
import type { GetItemsWithTypeNamesOptions } from '../services/items'

const { init } = useToast()

/** Ignore a stale list response when filters or warehouse context change mid-request. */
let itemsListRequest = 0

export const useItemsStore = defineStore('itemStore', {
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
    items: [] as InventoryItemApi[],
    item: null as InventoryItemApi | null,
  }),
  getters: {
    getItems(state) {
      return state.items
    },
  },
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setItems(items: InventoryItemApi[]) {
      this.items = items
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    addItem(item: any) {
      this.items.unshift(item)
    },
    editItem(editedItem: any) {
      const itemIndex = this.items.findIndex((item) => item.uuid == editedItem?.uuid)
      if (itemIndex !== -1) {
        this.items[itemIndex] = { ...this.items[itemIndex], ...editedItem }
      }
    },
    /** Merge partial fields into a list row (e.g. after unit packaging updates). */
    patchItem(itemUuid: string, patch: Record<string, unknown>) {
      const itemIndex = this.items.findIndex((item) => item.uuid == itemUuid)
      if (itemIndex !== -1) {
        this.items[itemIndex] = { ...this.items[itemIndex], ...patch }
      }
    },
    /** Re-fetch a single list row from the list endpoint so all columns (incl. stock) stay in sync. */
    async refreshItemRow(itemUuid: string) {
      if (!this.items.some((item) => item.uuid == itemUuid)) return
      const { status, data } = await API.items.getItems({ column: 'uuid', search: itemUuid }, { page: 1, perPage: 1 })
      const row = data?.data?.find((item: InventoryItemApi) => item.uuid == itemUuid)
      if (status === HttpStatusCode.Ok && row) this.patchItem(itemUuid, row)
    },
    removeItem(itemUuid: string) {
      const foundItemIndex = this.items.findIndex((item) => item.uuid == itemUuid)
      if (foundItemIndex === -1) return
      this.items.splice(foundItemIndex, 1)
    },
    async dispatchGetItems(filters?: Filters): Promise<any> {
      const generation = ++itemsListRequest
      this.initLoading()

      try {
        const { status, data } = await API.items.getItems(filters, this.pagination)
        if (generation !== itemsListRequest) {
          return { success: true, data: this.items, stale: true }
        }

        if (status === HttpStatusCode.Ok) {
          this.setItems(data.data)
          this.setPagination({
            meta: data.meta,
          })
        }

        return {
          success: status == HttpStatusCode.Ok,
          data: data.data,
        }
      } finally {
        if (generation === itemsListRequest) {
          this.stopLoading()
        }
      }
    },
    async dispatchGetItemsWithTypeNames(filters?: Filters, options?: GetItemsWithTypeNamesOptions): Promise<any> {
      this.initLoading()

      const { status, data } = await API.items
        .getItemsWithTypeNames(filters, this.pagination, options)
        .finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        this.setItems(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: status == HttpStatusCode.Ok,
        data: data.data,
      }
    },
    async dispatchCreateItem(input: InventoryCreateItemApi): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.items.createItem(input).finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        this.addItem(data.data)
        init({ message: 'Item created successfully', color: 'success' })
      }

      return {
        success: status == HttpStatusCode.Ok,
      }
    },
    async dispatchDeleteItem(item: InventoryItemApi): Promise<APIResponse<null>> {
      this.initLoading()

      const { status } = await API.items.deleteItem(item.uuid).finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        this.removeItem(item.uuid)
        init({ message: 'Item Deleted successfully', color: 'success' })
      }

      return {
        success: status == HttpStatusCode.Ok,
      }
    },
    async dispatchUpdateItem(input: InventoryItemApi): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.items.updateItem(input).finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        this.editItem(data.data)
        init({ message: 'Item Updated successfully', color: 'success' })
      }

      return {
        success: status === HttpStatusCode.Ok,
      }
    },
    async dispatchGetItemsOfItemType(itemTypeUuid: string): Promise<any> {
      this.initLoading()

      const { status, data } = await API.items.getItemsOfItemType(itemTypeUuid).finally(this.stopLoading)

      return {
        success: status === HttpStatusCode.Ok,
        data: data.data,
      }
    },
  },
})
